import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useState, useEffect } from 'react'
import { Link } from 'wouter'
import { ArrowLeft, User, Building, Mail, Phone, Globe, Linkedin, Edit, Save, Plus, Trash2, Crown, Calendar, CreditCard } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { buildApiUrl } from '@/config/api'
import { SubscriptionModal } from '@/components/subscription-modal'
import toast from 'react-hot-toast'

interface UserProfile {
  id: string
  name: string
  email: string
  company: string
  role: string
  phone?: string
  website?: string
  linkedinUrl?: string
  bio?: string
  skills: string[]
  experience: string[]
  education: string[]
  avatar?: string
  authProvider?: string
  subscriptionStatus: 'FREE' | 'ACTIVE' | 'CANCELLED' | 'EXPIRED' | 'PAST_DUE'
  subscriptionPlan?: 'FREE' | 'PRO'
  subscriptionStartDate?: string
  subscriptionEndDate?: string
  createdAt: string
  updatedAt: string
}

interface CompanyProfile {
  id: string
  name: string
  description?: string
  industry: string
  size: string
  founded?: string
  website?: string
  linkedinUrl?: string
  location?: string
  logo?: string
}

// Heuristic: if value contains spaces or non-cuid characters, treat as name
const looksLikeCompanyId = (value: string) => /^[a-z0-9]{20,}$/i.test(value) && !value.includes(' ')

export function UserProfile() {
  const { updateUser } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [company, setCompany] = useState<CompanyProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [isCompanyEditing, setIsCompanyEditing] = useState(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false)
  const [subscriptionData, setSubscriptionData] = useState<any>(null)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    website: '',
    linkedinUrl: '',
    bio: '',
    skills: [] as string[],
    experience: [] as string[],
    education: [] as string[],
  })

  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    description: '',
    industry: '',
    size: '',
    founded: '',
    website: '',
    linkedinUrl: '',
    location: '',
  })

  // Fetch subscription data
  const fetchSubscriptionData = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(buildApiUrl('/api/subscription/status'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setSubscriptionData(data.data)
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error)
    }
  }

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          toast.error('Please log in to access your profile')
          return
        }

        const response = await fetch(buildApiUrl('/api/users/profile'), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          setProfile(data.data)
          setFormData({
            name: data.data.name || '',
            email: data.data.email || '',
            company: data.data.company || '',
            role: data.data.role || '',
            phone: data.data.phone || '',
            website: data.data.website || '',
            linkedinUrl: data.data.linkedinUrl || '',
            bio: data.data.bio || '',
            skills: data.data.skills || [],
            experience: data.data.experience || [],
            education: data.data.education || [],
          })

          // Fetch company profile if user has a company
          if (data.data.company) {
            try {
              const companyRef = data.data.company as string
              const endpoint = looksLikeCompanyId(companyRef)
                ? buildApiUrl(`/api/companies/${companyRef}`)
                : buildApiUrl(`/api/companies/name/${encodeURIComponent(companyRef)}`)

              const companyResponse = await fetch(endpoint, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              })
              
              if (companyResponse.ok) {
                const companyData = await companyResponse.json()
                setCompany(companyData.data)
                setCompanyFormData({
                  name: companyData.data.name || '',
                  description: companyData.data.description || '',
                  industry: companyData.data.industry || '',
                  size: companyData.data.size || '',
                  founded: companyData.data.founded || '',
                  website: companyData.data.website || '',
                  linkedinUrl: companyData.data.linkedinUrl || '',
                  location: companyData.data.location || '',
                })
              } else if (companyResponse.status === 404) {
                console.warn('Company not found, user may have invalid company reference:', companyRef)
              } else {
                console.error('Failed to fetch company data:', companyResponse.status)
              }
            } catch (error) {
              console.error('Error fetching company data:', error)
            }
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error)
        toast.error('Unable to load profile. Please check your connection and try again.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchProfile()
    fetchSubscriptionData()
  }, [])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleCompanyInputChange = (field: string, value: string) => {
    setCompanyFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addSkill = () => {
    setFormData(prev => ({
      ...prev,
      skills: [...prev.skills, '']
    }))
  }

  const removeSkill = (index: number) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }))
  }

  const updateSkill = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.map((skill, i) => i === index ? value : skill)
    }))
  }

  const addExperience = () => {
    setFormData(prev => ({
      ...prev,
      experience: [...prev.experience, '']
    }))
  }

  const removeExperience = (index: number) => {
    setFormData(prev => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index)
    }))
  }

  const updateExperience = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      experience: prev.experience.map((exp, i) => i === index ? value : exp)
    }))
  }

  const addEducation = () => {
    setFormData(prev => ({
      ...prev,
      education: [...prev.education, '']
    }))
  }

  const removeEducation = (index: number) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }))
  }

  const updateEducation = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.map((edu, i) => i === index ? value : edu)
    }))
  }

  const fetchLinkedInData = async () => {
    setIsLinkedInLoading(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to access your profile')
        return
      }

      const response = await fetch(buildApiUrl('/api/users/linkedin-sync'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setFormData(prev => ({
          ...prev,
          name: data.data.name || prev.name,
          company: data.data.company || prev.company,
          role: data.data.role || prev.role,
          bio: data.data.bio || prev.bio,
          skills: data.data.skills || prev.skills,
          experience: data.data.experience || prev.experience,
          education: data.data.education || prev.education,
          linkedinUrl: data.data.linkedinUrl || prev.linkedinUrl,
        }))
        
        if (data.companyInfo) {
          toast.success(`LinkedIn data imported successfully! Company "${data.companyInfo.name}" ${data.companyInfo.created ? 'created' : 'found'}.`)
        } else if (data.warning) {
          toast.success(data.message, { duration: 4000 })
          toast(data.warning, { icon: '⚠️', duration: 6000 })
        } else {
          toast.success(data.message || 'LinkedIn data imported successfully!')
        }
      } else {
        const errorData = await response.json()
        if (errorData.requiresLinkedInAuth) {
          toast.error('Please log in with LinkedIn first to sync your profile data.', { duration: 6000 })
        } else {
          toast.error(errorData.error || 'Failed to import LinkedIn data')
        }
      }
    } catch (error) {
      console.error('Error fetching LinkedIn data:', error)
      toast.error('Failed to import LinkedIn data')
    } finally {
      setIsLinkedInLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to access your profile')
        return
      }

      const response = await fetch(buildApiUrl('/api/users/profile'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        const data = await response.json()
        setProfile(data.data)
        setIsEditing(false)
        toast.success('Profile updated successfully!')
        // Update auth context
        if (updateUser) {
          updateUser(data.data)
        }
      } else {
        const errorData = await response.json()
        toast.error(errorData.error || 'Failed to update profile')
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error('Failed to update profile')
    }
  }

  const handleCompanySave = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to access your profile')
        return
      }

      const url = company ? buildApiUrl(`/api/companies/${company.id}`) : buildApiUrl('/api/companies')
      const method = company ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(companyFormData),
      })

      if (response.ok) {
        const data = await response.json()
        setCompany(data.data)
        setIsCompanyEditing(false)
        toast.success(company ? 'Company profile updated successfully!' : 'Company profile created successfully!')
      } else if (response.status === 400) {
        const errorData = await response.json()
        if (errorData.details && Array.isArray(errorData.details)) {
          const errorMessages = errorData.details.map((detail: any) => detail.message).join(', ')
          toast.error(`Validation error: ${errorMessages}`)
        } else {
          toast.error(errorData.error || 'Please check your input and try again')
        }
      } else if (response.status === 401) {
        toast.error('Please log in to update your company profile')
      } else if (response.status === 403) {
        toast.error('You do not have permission to edit this company')
      } else if (response.status === 404) {
        toast.error('Company not found')
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }))
        console.error('Company update error:', errorData)
        toast.error(errorData.error || 'Unable to update company profile. Please try again.')
      }
    } catch (error) {
      console.error('Error updating company profile:', error)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        toast.error('Unable to connect to the server. Please check your internet connection and try again.')
      } else {
        toast.error('An unexpected error occurred. Please try again.')
      }
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link href="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
            <p className="text-muted-foreground">
              Manage your personal and company information
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          {profile?.authProvider === 'linkedin' && (
            <Button
              variant="outline"
              onClick={fetchLinkedInData}
              disabled={isLinkedInLoading}
            >
              <Linkedin className="mr-2 h-4 w-4" />
              {isLinkedInLoading ? 'Syncing...' : 'Sync LinkedIn'}
            </Button>
          )}
          <Button
            onClick={isEditing ? handleSave : () => setIsEditing(true)}
          >
            {isEditing ? (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            ) : (
              <>
                <Edit className="mr-2 h-4 w-4" />
                Edit Profile
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Personal Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>Personal Information</span>
            </CardTitle>
            <CardDescription>
              Your personal details and professional information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter your phone number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Website</label>
                  <Input
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="Enter your website URL"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">LinkedIn URL</label>
                  <Input
                    value={formData.linkedinUrl}
                    onChange={(e) => handleInputChange('linkedinUrl', e.target.value)}
                    placeholder="Enter your LinkedIn profile URL"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Bio</label>
                  <Textarea
                    value={formData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Tell us about yourself..."
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Skills</label>
                  <div className="space-y-2">
                    {formData.skills.map((skill, index) => (
                      <div key={index} className="flex space-x-2">
                        <Input
                          value={skill}
                          onChange={(e) => updateSkill(index, e.target.value)}
                          placeholder="Enter a skill"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => removeSkill(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addSkill}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Skill
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Experience</label>
                  <div className="space-y-2">
                    {formData.experience.map((exp, index) => (
                      <div key={index} className="flex space-x-2">
                        <Textarea
                          value={exp}
                          onChange={(e) => updateExperience(index, e.target.value)}
                          placeholder="Describe your experience..."
                          rows={2}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => removeExperience(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addExperience}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Experience
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Education</label>
                  <div className="space-y-2">
                    {formData.education.map((edu, index) => (
                      <div key={index} className="flex space-x-2">
                        <Textarea
                          value={edu}
                          onChange={(e) => updateEducation(index, e.target.value)}
                          placeholder="Describe your education..."
                          rows={2}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => removeEducation(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addEducation}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Education
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Name:</span>
                  <span className="font-medium">{profile?.name}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Email:</span>
                  <span className="font-medium">{profile?.email}</span>
                </div>

                {profile?.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Phone:</span>
                    <span className="font-medium">{profile.phone}</span>
                  </div>
                )}

                {profile?.website && (
                  <div className="flex items-center space-x-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Website:</span>
                    <a href={profile.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                      {profile.website}
                    </a>
                  </div>
                )}

                {profile?.linkedinUrl && (
                  <div className="flex items-center space-x-2">
                    <Linkedin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">LinkedIn:</span>
                    <a href={profile.linkedinUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                      View Profile
                    </a>
                  </div>
                )}

                {profile?.bio && (
                  <div>
                    <h4 className="font-medium mb-2">Bio</h4>
                    <p className="text-sm text-muted-foreground">{profile.bio}</p>
                  </div>
                )}

                {profile?.skills && profile.skills.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Skills</h4>
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.map((skill, index) => (
                        <span key={index} className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {profile?.experience && profile.experience.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Experience</h4>
                    <div className="space-y-2">
                      {profile.experience.map((exp, index) => (
                        <p key={index} className="text-sm text-muted-foreground">{exp}</p>
                      ))}
                    </div>
                  </div>
                )}

                {profile?.education && profile.education.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Education</h4>
                    <div className="space-y-2">
                      {profile.education.map((edu, index) => (
                        <p key={index} className="text-sm text-muted-foreground">{edu}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Company Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Building className="h-5 w-5" />
              <span>Company Information</span>
            </CardTitle>
            <CardDescription>
              Your company details and business information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Company Profile</span>
              <Button
                variant="outline"
                size="sm"
                onClick={isCompanyEditing ? handleCompanySave : () => setIsCompanyEditing(true)}
              >
                {isCompanyEditing ? (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </>
                ) : (
                  <>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </>
                )}
              </Button>
            </div>

            {isCompanyEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Company Name</label>
                  <Input
                    value={companyFormData.name}
                    onChange={(e) => handleCompanyInputChange('name', e.target.value)}
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Textarea
                    value={companyFormData.description}
                    onChange={(e) => handleCompanyInputChange('description', e.target.value)}
                    placeholder="Describe your company..."
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Industry</label>
                  <Input
                    value={companyFormData.industry}
                    onChange={(e) => handleCompanyInputChange('industry', e.target.value)}
                    placeholder="Enter industry"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Company Size</label>
                  <select
                    value={companyFormData.size}
                    onChange={(e) => handleCompanyInputChange('size', e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  >
                    <option value="">Select size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Founded Year</label>
                  <Input
                    type="number"
                    value={companyFormData.founded}
                    onChange={(e) => handleCompanyInputChange('founded', e.target.value)}
                    placeholder="Enter founded year"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Website</label>
                  <Input
                    value={companyFormData.website}
                    onChange={(e) => handleCompanyInputChange('website', e.target.value)}
                    placeholder="Enter company website"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">LinkedIn URL</label>
                  <Input
                    value={companyFormData.linkedinUrl}
                    onChange={(e) => handleCompanyInputChange('linkedinUrl', e.target.value)}
                    placeholder="Enter company LinkedIn URL"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Location</label>
                  <Input
                    value={companyFormData.location}
                    onChange={(e) => handleCompanyInputChange('location', e.target.value)}
                    placeholder="Enter company location"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {company ? (
                  <>
                    <div className="flex items-center space-x-2">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Company:</span>
                      <span className="font-medium">{company.name}</span>
                    </div>

                    {company.description && (
                      <div>
                        <h4 className="font-medium mb-2">Description</h4>
                        <p className="text-sm text-muted-foreground">{company.description}</p>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">Industry:</span>
                      <span className="font-medium">{company.industry}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">Size:</span>
                      <span className="font-medium">{company.size}</span>
                    </div>

                    {company.founded && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground">Founded:</span>
                        <span className="font-medium">{company.founded}</span>
                      </div>
                    )}

                    {company.website && (
                      <div className="flex items-center space-x-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Website:</span>
                        <a href={company.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                          {company.website}
                        </a>
                      </div>
                    )}

                    {company.linkedinUrl && (
                      <div className="flex items-center space-x-2">
                        <Linkedin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">LinkedIn:</span>
                        <a href={company.linkedinUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                          View Company
                        </a>
                      </div>
                    )}

                    {company.location && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground">Location:</span>
                        <span className="font-medium">{company.location}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Company Profile</h3>
                    <p className="text-muted-foreground mb-4">
                      Create a company profile to showcase your business
                    </p>
                    <Button onClick={() => setIsCompanyEditing(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Company Profile
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Crown className="h-5 w-5" />
              <span>Subscription</span>
            </CardTitle>
            <CardDescription>
              Manage your subscription and billing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {subscriptionData ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Current Plan:</span>
                    <span className={`font-medium px-2 py-1 rounded-full text-xs ${
                      subscriptionData.hasActiveSubscription 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {subscriptionData.subscriptionPlan === 'PRO' ? 'Pro Plan' : 'Free Plan'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <span className={`font-medium px-2 py-1 rounded-full text-xs ${
                      subscriptionData.subscriptionStatus === 'ACTIVE' 
                        ? 'bg-green-100 text-green-800'
                        : subscriptionData.subscriptionStatus === 'CANCELLED'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {subscriptionData.subscriptionStatus}
                    </span>
                  </div>
                </div>

                {subscriptionData.subscriptionPlan === 'PRO' && (
                  <>
                    {subscriptionData.subscriptionStartDate && (
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Started:</span>
                        <span className="font-medium">
                          {new Date(subscriptionData.subscriptionStartDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}

                    {subscriptionData.subscriptionEndDate && (
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Next billing:</span>
                        <span className="font-medium">
                          {new Date(subscriptionData.subscriptionEndDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}

                    <div className="pt-4 border-t">
                      <Button 
                        variant="outline" 
                        onClick={() => setShowSubscriptionModal(true)}
                        className="w-full"
                      >
                        Manage Subscription
                      </Button>
                    </div>
                  </>
                )}

                {subscriptionData.subscriptionPlan !== 'PRO' && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">Upgrade to Pro</h4>
                      <p className="text-sm text-blue-700 mb-3">
                        Get access to publish RFPs, respond to RFPs, and advanced features.
                      </p>
                      <div className="text-lg font-bold text-blue-900 mb-3">
                        $199/month
                      </div>
                      <Button 
                        onClick={() => setShowSubscriptionModal(true)}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        Upgrade to Pro
                      </Button>
                    </div>

                    <div className="text-xs text-muted-foreground text-center">
                      Cancel anytime.
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Plan Features</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      <span>Browse RFPs</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      <span>Add to watchlist</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`w-2 h-2 rounded-full ${subscriptionData.subscriptionPlan === 'PRO' ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                      <span className={subscriptionData.subscriptionPlan === 'PRO' ? '' : 'text-muted-foreground'}>
                        Publish RFPs
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`w-2 h-2 rounded-full ${subscriptionData.subscriptionPlan === 'PRO' ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                      <span className={subscriptionData.subscriptionPlan === 'PRO' ? '' : 'text-muted-foreground'}>
                        Respond to RFPs
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`w-2 h-2 rounded-full ${subscriptionData.subscriptionPlan === 'PRO' ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                      <span className={subscriptionData.subscriptionPlan === 'PRO' ? '' : 'text-muted-foreground'}>
                        Advanced analytics
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`w-2 h-2 rounded-full ${subscriptionData.subscriptionPlan === 'PRO' ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                      <span className={subscriptionData.subscriptionPlan === 'PRO' ? '' : 'text-muted-foreground'}>
                        Priority support
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Loading Subscription</h3>
                <p className="text-muted-foreground">
                  Loading your subscription information...
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false)
          fetchSubscriptionData() // Refresh subscription data when modal closes
        }}
        feature="publish"
        currentPlan={subscriptionData?.subscriptionPlan as 'FREE' | 'PRO'}
        hasActiveSubscription={subscriptionData?.hasActiveSubscription || false}
      />
    </div>
  )
} 